/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {NextIcon} from '@ion/dls/src/icons/NextIcon';
import {PlayIcon} from '@ion/dls/src/icons/PlayIcon';
import {PreviousIcon} from '@ion/dls/src/icons/PreviousIcon';
import {SystemPauseIcon} from '@ion/dls/src/icons/SystemPauseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {dateTimeFormatter} from '@ion/dls/src/utils/ComponentUtils';
import Tooltip from '../../components/tooltip/Tooltip';
import {
  END_EVENT_NODE_LAYER,
  IN_TRIP_NODE_LAYER,
  IN_TRIP_PATH_LAYER
} from '../constants';
import {
  initializeEsriModules
} from '../helpers/initializeEsriModules';
import {
  applyFilterToLayers,
  getLayerById
} from '../helpers/MapViewUtils';
import {
  getBestTimeDiff,
  isPlayMode,
  isReadyMode,
  isValidAdvancement,
  PlayDirection,
  ViewModelState
} from '../helpers/TimeSliderUtils';

type TimeSliderProps = {
  map?: __esri.Map;
  view?: __esri.MapView;
  timeline?: any;
  assetsHistory?: any[];
}

const useStyles = makeStyles(() => ({
  container : {
    display         : 'flex',
    flexDirection   : 'column',
    position        : 'absolute',
    zIndex          : 3,
    backgroundColor : 'white',
    width           : '330px',
    height          : '145px',
    alignContent    : 'center',
    marginTop       : '-30px',
    boxShadow       : '1px 1px 10px rgb(0 0 0 / 30%)'

  },
  toolContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignContent   : 'center'
  },
  assetNameText : {
    fontSize       : '18px',
    display        : 'flex',
    width          : '250px',
    flexDirection  : 'row',
    justifyContent : 'left',
    alignSelf      : 'left',
    marginLeft     : '17px',
    marginTop      : '9px'
  },
  controlsContainer : {
    width  : '100%',
    height : '100%'
  },
  controls : {
    display        : 'flex',
    width          : '130px',
    height         : '30px',
    position       : 'absolute',
    marginTop      : '-35px',
    left           : '90px',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignContent   : 'center',
    background     : 'white'
  },
  controlButton : {
    padding        : 0,
    border         : 'none',
    background     : 'white',
    alignContent   : 'center',
    justifyContent : 'center',
    '&:hover'      : {
      background : dlsColor('grey6'),
      boxShadow  : 'none'
    },
    '&:active' : {
      backgroundColor : dlsColor('paleBlue')
    }
  }
}));

export const TimeSliderContainer = ({map, view, timeline, assetsHistory}: TimeSliderProps) => {
  const [sliderPlaying, setSliderPlaying] = useState(false);
  useEffect(() => {
    setInitialView();
  }, [assetsHistory]);
  const assetName = assetsHistory[0]?.assetName;

  let timeSlider;
  const setInitialView = async() => {
    const [TimeSlider] = await initializeEsriModules(
      'esri/widgets/TimeSlider'
    );
    timeSlider   = await createTimeSlider(TimeSlider);
    const nodeLayer    = getLayerById((map as any), IN_TRIP_NODE_LAYER);
    const pathLayer    = getLayerById((map as any), IN_TRIP_PATH_LAYER);
    const endNodeLayer = getLayerById((map as any), END_EVENT_NODE_LAYER);

    timeSlider.watch('timeExtent', (val) => {
      const filterOption = `eventTime <= ${Date.parse(val.end)}`;
      const layerList    = [nodeLayer, pathLayer, endNodeLayer];
      layerList.forEach(layer => {
        if (layer) {
          view.whenLayerView(layer).then(() => {
            applyFilterToLayers({
              view,
              layers : [layer],
              filterOption
            });
          });
        }
      });
      const slider = timeSlider?._slider;
      const sliderTime = new Date(slider?.values?.[0]);
      sliderTime?.setSeconds(0, 0);
      const fullTimeExtentEndTime = timeSlider?.fullTimeExtent?.end?.getTime();
      if ((fullTimeExtentEndTime - 1000 * 60) === sliderTime?.getTime() ||
      fullTimeExtentEndTime === sliderTime?.getTime()) {
        timeSlider.stop();
        setSliderPlaying(false);
      }
    });
  };

  const createTimeSlider = async(TimeSlider) => {
    const start = new Date(assetsHistory[0]?.eventTime);
    start.setSeconds(0, 0);
    const end   = new Date(assetsHistory.at(-1)?.eventTime);
    end.setSeconds(0, 0);
    const timeSlider = new TimeSlider({
      container      : 'timeSliderDiv',
      view,
      mode           : 'cumulative-from-start',
      fullTimeExtent : {
        start : start,
        end   : end
      },
      values : assetsHistory.map(({eventTime}) => {
        const date = new Date(eventTime);
        date?.setSeconds(0, 0);
        return date?.getTime();
      }),
      stops : {
        interval : {
          value : setStopInterval(start, end),
          unit  : 'minutes'
        }
      },
      playRate            : 1000,
      labelFormatFunction : (value, type, element) => {
        const dateTimeValue = value?.[0] || value?.[1];
        switch (type) {
          case 'extent':
            element.innerText = `${dateTimeFormatter(dateTimeValue?.getTime())}`;
            break;
        }
      }
    });
    return timeSlider;
  };

  const handlePlayRate = useCallback(({target:{value: playRate}}) => {
    const current = timeSlider;
    if (current) {
      const {viewModel: {state = ViewModelState.Ready} = {}} = current;
      current.set({playRate, loop: false});
      if (isPlayMode(state)) {
        current.play();
        setTimeout(() => {
          if (isReadyMode(current?.viewModel?.state)) {
            current.play();
          }
        }, 0);
      } else if (isReadyMode(state)) {
        current.stop();
      }
    }
  }, []);

  const setStopInterval = useCallback((start, end) => {
    // default to be 1 min for date range less than 24 hours, else 10 min on each tick.
    return (end - start) / (1000 * 60 * 60 * 24) <= 1 ? 1 : 10;
  }, []);

  const togglePlay = useCallback(async() => {
    const viewState = timeSlider?.viewModel?.state;
    if (isReadyMode(viewState)) {
      timeSlider.play();
    } else if (isPlayMode(viewState)) {
      timeSlider.stop();
    }
    setSliderPlaying(current => !current);
  }, []);

  const onPlayProgress = useCallback((direction: PlayDirection) => () => {
    const slider =  timeSlider?._slider;
    if (slider) {
      const dates  = assetsHistory.map(({eventTime}) => {
        const date = new Date(eventTime);
        date?.setSeconds(0, 0);
        return date?.getTime();
      }) || [];
      const sliderCurrentTime    = new Date(timeSlider?._slider?.values?.[0]);
      sliderCurrentTime?.setSeconds(0, 0);
      const now = sliderCurrentTime?.getTime();
      let diff     = getBestTimeDiff(direction);
      let position = dates.length;

      dates.forEach((date, i) => {
        const difference = now - date;
        if (isValidAdvancement(difference, diff, direction)) {
          diff = difference;
          position = i;
        }
      });
      slider.values = [dates[position] || slider.values[0]];
    }
  }, []);

  const classes = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.toolContainer}>
        <span className={classes.assetNameText}>
          {/* only display up to 20 charaters */}
          {assetName.slice(0, 20)}
          {assetName.length > 20 && (<span>...</span>)}
        </span>
        <Select defaultValue={1000}
          onChange={handlePlayRate}
          disableUnderline>
          <MenuItem value={1000}>1x</MenuItem>
          <MenuItem value={500}>2x</MenuItem>
          <MenuItem value={333}>3x</MenuItem>
        </Select>
      </div>
      <div id='timeSliderDiv' />
      <div className={classes.controlsContainer}>
        <div className={classes.controls}>
          <Tooltip arrow title={translate('maps:timeSlider.skipToPreviousEvent')}>
            <button className={classes.controlButton} onClick={onPlayProgress(PlayDirection.Previous)}>
              <PreviousIcon size={IconSize.medium} />
            </button>
          </Tooltip>
          <button className={classes.controlButton} onClick={togglePlay}>
            {sliderPlaying ? <SystemPauseIcon size={IconSize.medium} /> : <PlayIcon size={IconSize.medium} />}
          </button>
          <Tooltip arrow title={translate('maps:timeSlider.skipToNextEvent')}>
            <button className={classes.controlButton} onClick={onPlayProgress(PlayDirection.Next)}>
              <NextIcon size={IconSize.medium} />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
